import React from 'react';
import upperFirst from 'lodash/upperFirst';
import { connect } from 'react-redux';
import { RouteComponentProps, useLocation } from 'react-router';
import Metadata from '@om-fe/core/components/metadata/metadata';
import { AppState } from '@om-fe/core/core/types';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { PlatformTypes } from '@om-fe/utils';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import { RouteNames, RouteModel } from '@om-fe/core/core/routing/utils';
import { UseSportsNavStore, useSportsNavTopicStoreCreator } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks';
import { SportsNavType, SportsNavCounterType } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.constants';
import { TopBetsMatchesContent } from '../components/match-content-tabs/top-bets-content-tabs';
import { useEMRouting } from '@om-fe/core/core/routing/with-routing';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';

interface SportLocationEsportPageStoreProps {
    readonly isLarge?: boolean;
    readonly breakpoints?: Breakpoints;
    readonly deviceType?: PlatformTypes;
    readonly routeName: RouteNames | undefined;
}

export type SportLocationEsportPageProps = SportLocationEsportPageStoreProps
    & RouteComponentProps<{
    readonly disciplineId: string;
    readonly disciplineName: string;
    readonly sportLocationId?: string;
    readonly sportLocationName?: string;
    readonly sportChildId?: string;
    readonly activeTab?: string;
}>;

function SportLocationEsportPageComponent(
    {
        match: {
            params: {
                disciplineId,
                disciplineName,
                sportLocationId,
                sportLocationName,
                sportChildId,
            },
        },
        isLarge,
    }: SportLocationEsportPageProps,
) {
    const config: OMFE2ConfigModel = getConfig();
    const topicTimezoneOffset: boolean = config.matchListConfig?.topBets?.topicTimezoneOffset ?? false;
    const enabledBetSimulate: boolean = config.simulate?.enabled ?? false;
    const useStore: UseSportsNavStore = useSportsNavTopicStoreCreator({
        sportsNavType: SportsNavType.TopBets,
        showOnlySportsWithMatches: true,
        counterType: SportsNavCounterType.NumberOfEvents,
        showFromAPI: true,
        topicTimezoneOffset,
        enabledBetSimulate,
    });
    const metadataText: string = `${upperFirst(disciplineName)} ${upperFirst(sportLocationName)}`;
    const currentRoute: RouteModel | undefined = useEMRouting().getRouteByUrl(useLocation().pathname);
    const isEsportsPage: boolean = currentRoute?.isEsportsRoute ? true : false;

    return (
        <UserBehaviorTrackingContext.Provider value={{ userBehaviorTrackingPage: RouteNames.sportLocation, userBehaviorTrackingLocation: UserBehaviorTrackingLocation.GroupedMatchList }}>
            <Metadata
                title={metadataText}
                keywords={`${disciplineName}, ${sportLocationName}`}
                description={metadataText}
            />
            <Breadcrumbs
                disciplineId={sportChildId ? sportChildId : disciplineId}
                locationId={sportLocationId}
                isLarge={isLarge}
                routeType={DisciplineRouteType.Location}
            />
            <TopBetsMatchesContent
                isEsportsPage={isEsportsPage}
                allSportsIsSelected={false}
                useStore={useStore}
            />
        </UserBehaviorTrackingContext.Provider>
    );
}

const SportLocationEsport: React.ComponentType<SportLocationEsportPageStoreProps> = connect<SportLocationEsportPageStoreProps, null, SportLocationEsportPageProps, AppState>(
    (state: AppState): SportLocationEsportPageStoreProps => {
        return {
            isLarge: (state.breakpoints.large || state.breakpoints.xl),
            breakpoints: state.breakpoints,
            deviceType: state.deviceInfo?.platform.type,
            routeName: state.routing.name,
        };
    })(SportLocationEsportPageComponent);
export default SportLocationEsport;